// src/contexts/NotificationContext.js - FIXED VERSION
import React, { createContext, useContext, useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';
import useAuth from '../hooks/useAuth';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Fetch unread count
  const fetchUnreadCount = async () => {
    // FIXED: Don't fetch if not authenticated or still loading auth
    if (!isAuthenticated || authLoading) return;
    
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      // FIXED: Reset count on error (likely auth issue)
      setUnreadCount(0);
    }
  };

  // Fetch notifications
  const fetchNotifications = async (page = 1, pageSize = 20) => {
    // FIXED: Don't fetch if not authenticated or still loading auth
    if (!isAuthenticated || authLoading) return { results: [], unread_count: 0, has_next: false };
    
    try {
      setIsLoading(true);
      const data = await notificationService.getNotifications(page, pageSize);
      
      if (page === 1) {
        setNotifications(data.results);
      } else {
        setNotifications(prev => [...prev, ...data.results]);
      }
      
      setUnreadCount(data.unread_count);
      return data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      // FIXED: Reset on error
      if (page === 1) {
        setNotifications([]);
        setUnreadCount(0);
      }
      return { results: [], unread_count: 0, has_next: false };
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    // FIXED: Don't execute if not authenticated
    if (!isAuthenticated || authLoading) return;
    
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state immediately
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true }
            : notification
        )
      );
      
      // Update unread count immediately
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    // FIXED: Don't execute if not authenticated
    if (!isAuthenticated || authLoading) return;
    
    try {
      await notificationService.markAllAsRead();
      
      // Update local state immediately
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
      
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // NEW: Bulk mark as read
  const bulkMarkAsRead = async (notificationIds) => {
    // FIXED: Don't execute if not authenticated
    if (!isAuthenticated || authLoading) return;
    
    try {
      const result = await notificationService.bulkMarkAsRead(notificationIds);
      
      // Update local state immediately
      setNotifications(prev => 
        prev.map(notification => 
          notificationIds.includes(notification.id) 
            ? { ...notification, is_read: true }
            : notification
        )
      );
      
      // Update unread count
      const unreadToMark = notifications.filter(n => 
        notificationIds.includes(n.id) && !n.is_read
      ).length;
      setUnreadCount(prev => Math.max(0, prev - unreadToMark));
      
      return result;
    } catch (error) {
      console.error('Failed to bulk mark notifications as read:', error);
      throw error;
    }
  };

  // NEW: Bulk delete
  const bulkDelete = async (notificationIds) => {
    // FIXED: Don't execute if not authenticated
    if (!isAuthenticated || authLoading) return;
    
    try {
      const result = await notificationService.bulkDelete(notificationIds);
      
      // Update local state immediately
      const deletedNotifications = notifications.filter(n => notificationIds.includes(n.id));
      const unreadDeleted = deletedNotifications.filter(n => !n.is_read).length;
      
      setNotifications(prev => 
        prev.filter(notification => !notificationIds.includes(notification.id))
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - unreadDeleted));
      
      return result;
    } catch (error) {
      console.error('Failed to bulk delete notifications:', error);
      throw error;
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    // FIXED: Don't execute if not authenticated
    if (!isAuthenticated || authLoading) return;
    
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state immediately
      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
      
      // Update unread count if deleted notification was unread
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Refresh notifications (for manual refresh)
  const refreshNotifications = async () => {
    // FIXED: Don't execute if not authenticated
    if (!isAuthenticated || authLoading) return;
    
    await fetchUnreadCount();
    await fetchNotifications();
  };

  // FIXED: Better authentication handling
  useEffect(() => {
    // Clear everything when not authenticated or still loading
    if (!isAuthenticated || authLoading) {
      setUnreadCount(0);
      setNotifications([]);
      return;
    }

    // Wait for authentication to be fully established, then start fetching
    const timer = setTimeout(() => {
      fetchUnreadCount();
      
      // Set up interval for very frequent updates (5 seconds)
      const interval = setInterval(fetchUnreadCount, 5000);
      
      // Cleanup function
      return () => clearInterval(interval);
    }, 500); // Small delay to ensure auth is stable

    return () => {
      clearTimeout(timer);
    };
  }, [isAuthenticated, authLoading]);

  const value = {
    unreadCount,
    notifications,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    bulkMarkAsRead,     // NEW
    bulkDelete,         // NEW
    deleteNotification,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};